import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, Loader2, Check } from 'lucide-react';
import sprayService from '../../services/spray';

interface SpraySituation {
  id: number;
  name: string;
  description?: string | null;
}

interface SpraySituationComboboxProps {
  value: number | null | undefined;
  onChange: (id: number) => void;
  error?: string;
  disabled?: boolean;
}

function SpraySituationCombobox({ value, onChange, error, disabled }: SpraySituationComboboxProps) {
  const [situations, setSituations] = useState<SpraySituation[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    sprayService.getSituations().then(res => {
      if (res.success && res.data) setSituations(res.data as SpraySituation[]);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  const selected = situations.find(s => s.id === value);

  const filtered = search.trim()
    ? situations.filter(s => s.name.toLowerCase().includes(search.toLowerCase()))
    : situations;

  const exactMatch = situations.some(
    s => s.name.toLowerCase() === search.trim().toLowerCase()
  );
  const showAddNew = search.trim().length > 0 && !exactMatch && !adding;

  const handleSelect = (s: SpraySituation) => {
    onChange(s.id);
    setOpen(false);
    setSearch('');
  };

  const handleAddNew = async () => {
    const name = search.trim();
    if (!name) return;
    setAdding(true);
    setAddError(null);
    try {
      const res = await sprayService.createSituation(name);
      if (res.success && res.data) {
        const newSit = res.data as SpraySituation;
        // A shared situation of that name comes back instead of a copy (existing).
        setSituations(prev => (prev.some(p => p.id === newSit.id) ? prev : [...prev, newSit].sort((a, b) => a.name.localeCompare(b.name))));
        onChange(newSit.id);
        setOpen(false);
        setSearch('');
      } else {
        setAddError('Could not add situation');
      }
    } catch {
      setAddError('Could not add situation');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between border rounded-lg px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white transition-colors ${
          error
            ? 'border-red-300 focus:ring-red-400'
            : 'border-neutral-300 hover:border-neutral-400'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className={selected ? 'text-neutral-900' : 'text-neutral-400'}>
          {loading ? 'Loading…' : selected ? selected.name : 'Select situation…'}
        </span>
        {loading
          ? <Loader2 className="h-4 w-4 text-neutral-400 animate-spin" />
          : <ChevronDown className={`h-4 w-4 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        }
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-neutral-100">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && showAddNew) handleAddNew();
                if (e.key === 'Escape') { setOpen(false); setSearch(''); }
              }}
              placeholder="Search or type new situation…"
              className="w-full text-sm px-2 py-1.5 border border-neutral-200 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && !showAddNew && (
              <p className="text-sm text-neutral-400 text-center py-4">
                {search.trim() ? 'No match — press Enter to add' : 'Type to search or add a situation'}
              </p>
            )}

            {filtered.map(s => (
              <button
                key={s.id}
                type="button"
                onMouseDown={() => handleSelect(s)}
                className="w-full flex items-center justify-between text-left px-3 py-2 text-sm text-neutral-800 hover:bg-primary-50 hover:text-primary-700"
              >
                <span>{s.name}</span>
                {s.id === value && <Check className="h-4 w-4 text-primary-600 flex-shrink-0" />}
              </button>
            ))}

            {showAddNew && (
              <button
                type="button"
                onMouseDown={handleAddNew}
                disabled={adding}
                className="w-full flex items-center gap-1.5 text-left px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 border-t border-neutral-100 font-medium"
              >
                {adding
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Plus className="h-3.5 w-3.5" />
                }
                {adding ? 'Adding…' : <>Add "<span>{search.trim()}</span>"</>}
              </button>
            )}
          </div>

          {addError && (
            <p className="text-xs text-red-600 px-3 py-2 border-t border-neutral-100">{addError}</p>
          )}
        </div>
      )}

      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

export default SpraySituationCombobox;
